import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DashboardModal } from '../components/DashboardModal';
import { fetchResource } from 'api-provider/actions';
import __ from 'utils/i18n';
import { useSelector } from 'react-redux';
import { Button, Col, Row } from 'reactstrap';
import ImageQR from 'assets/qrestatico.svg';
import Loader from 'components/Loader';
import ErrorMessage from 'components/flashMessages/ErrorMessage';

const SubsidiaryQRModal = ({ isOpen, toggle, subsidiary }) => {
  const token = useSelector((state) => state.api.token);
  const [isLoading, setIsLoading] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);

  const getQRv4Resource = useCallback(() => ({
    endpoint: `v4/payments/qr/${subsidiary.defaultPointSaleUuid}`,
    method: 'POST',
    headers: {
      'X-Authentication-Token': token,
      'content-type': 'application/json',
      'Prefer': 'return=pdf-base64',
    },
    data: {
      type: 'GENERIC',
      source: 'QR',
      currency: '032',
    },
  }), [subsidiary?.defaultPointSaleUuid, token]);

  const fetchQRData = useCallback(() => {
    setIsLoading(true);
    setDownloadFailed(false);

    fetchResource(getQRv4Resource)
      .onSuccess((data) => {
        setPdfBase64(data.pdf_base64 || data.pdfBase64 || null);
        setIsLoading(false);
      })
      .onFailure(() => {
        setDownloadFailed(true);
        setIsLoading(false);
      });
  }, [getQRv4Resource]);

  useEffect(() => {
    if (isOpen && subsidiary?.defaultPointSaleUuid) {
      fetchQRData();
    }
    if (!isOpen) {
      setPdfBase64(null);
      setDownloadFailed(false);
    }
  }, [isOpen, subsidiary?.defaultPointSaleUuid, fetchQRData]);

  const handleDownload = () => {
    if (!pdfBase64) return;

    const byteCharacters = atob(pdfBase64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `QR-${subsidiary?.name || 'sucursal'}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const noPointOfSale = !subsidiary?.defaultPointSaleUuid;

  return (
    <DashboardModal
      showModal={isOpen}
      hideModal={() => toggle(false)}
      headerText={__('dashboard.sections.merchantSubsidiaries.qr.headerText')}
      cancelText={__('actions.close')}
      modalClass="subsidiary-qr-modal"
    >
      <Row className="box-info">
        <Col>
          <Row className="align-items-center">
            <Col lg="3" className="text-center">
              {isLoading ? (
                <Loader isLoading={true} />
              ) : (
                <img src={ImageQR} alt="QR" className="subsidiary-qr-icon" />
              )}
            </Col>
            <Col lg="6">
              <p className="subsidiary-qr-name">
                <strong>{subsidiary?.name}</strong>
              </p>
              {noPointOfSale ? (
                <p style={{ color: '#999999' }}>
                  {__('dashboard.sections.merchantSubsidiaries.qr.noPointOfSale')}
                </p>
              ) : (
                <>
                  <p style={{ color: '#333333' }}>
                    <strong>
                      {__('dashboard.sections.merchantSubsidiaries.qr.downloadAndPrint')}
                    </strong>
                  </p>
                  <p style={{ color: '#999999' }}>
                    {__('dashboard.sections.merchantSubsidiaries.qr.putOnAVisibleLocation')}
                  </p>
                </>
              )}
            </Col>
            <Col lg="3" className="text-center">
              {!isLoading && !noPointOfSale && (
                <Button
                  color="primary"
                  type="button"
                  onClick={handleDownload}
                  disabled={!pdfBase64}
                >
                  <i className="mdi mdi-download mdi-18px" />{' '}
                  <strong>{__('actions.download')}</strong>
                </Button>
              )}
            </Col>
          </Row>
        </Col>
      </Row>
      {downloadFailed && (
        <Row className="mt-3">
          <Col>
            <ErrorMessage>
              {__('dashboard.sections.merchantSubsidiaries.qr.downloadError')}
            </ErrorMessage>
            <Button
              color="link"
              onClick={fetchQRData}
              className="mt-2"
            >
              {__('actions.retry')}
            </Button>
          </Col>
        </Row>
      )}
    </DashboardModal>
  );
};

SubsidiaryQRModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  subsidiary: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    defaultPointSaleUuid: PropTypes.string,
  }),
};

export default SubsidiaryQRModal;
